/**
 * Perfis de processamento (01/08 — pedido do dono): a camada ACIMA do motor.
 * Cada perfil escolhe o motor do registry, ancora a vigência (a partir de qual
 * mês vale) e registra o MOTIVO da criação. A abertura da competência escolhe
 * o perfil — não mais o motor diretamente.
 *
 * A tela tem três blocos:
 * 1. Perfis cadastrados (motor, vigência, motivo, nº de competências pinadas;
 *    ativar/desativar, editar motivo/vigência fim — motor e vigência inicial
 *    são imutáveis, como toda vigência do sistema);
 * 2. Novo perfil (nome, motor entre os registrados, vigências, motivo);
 * 3. Motores registrados: o MANIFESTO de cada versão (mudanças × anterior e o
 *    motivo da versão nova) e o comparador com o DIFF ESTRUTURAL entre duas
 *    versões (fórmulas que mudaram, parâmetros +/-, suportadas +/-).
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Select,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { api } from "../api";
import { fmtCompetencia } from "../format";
import { ErroApiAlert, notificarErroApi, notificarOk } from "../componentes/notificacoes";
import { Carregando, Vazio } from "../componentes/Estados";

interface Perfil {
  id: string;
  nome: string;
  descricao: string | null;
  motorVersion: string;
  vigenciaInicio: number;
  vigenciaFim: number | null;
  motivo: string | null;
  ativo: boolean;
  _count?: { competencias: number };
}

interface MotorManifesto {
  versao: string;
  descricao: string;
  mudancas: string[];
  motivoNovaVersao: string;
  formulas: Record<string, string>;
}

interface DiffMotores {
  de: string;
  para: string;
  formulasAlteradas: { chave: string; de: string; para: string }[];
  parametrosExigidosAdicionados: string[];
  parametrosExigidosRemovidos: string[];
  suportadasAlteradas: { chave: string; adicionadas: string[]; removidas: string[] }[];
  mudancas: string[];
  motivoNovaVersao: string;
}

function rotuloVigencia(p: Perfil): string {
  return `${fmtCompetencia(p.vigenciaInicio)}${p.vigenciaFim ? ` a ${fmtCompetencia(p.vigenciaFim)}` : " em diante"}`;
}

export function PerfisPage() {
  const [perfis, setPerfis] = useState<Perfil[] | null>(null);
  const [motores, setMotores] = useState<MotorManifesto[]>([]);
  const [erro, setErro] = useState<unknown>(null);

  // novo perfil
  const [nome, setNome] = useState("");
  const [motor, setMotor] = useState("");
  const [vigInicio, setVigInicio] = useState("");
  const [vigFim, setVigFim] = useState("");
  const [motivo, setMotivo] = useState("");
  const [descricao, setDescricao] = useState("");

  // comparador de motores
  const [diffDe, setDiffDe] = useState("");
  const [diffPara, setDiffPara] = useState("");
  const [diff, setDiff] = useState<DiffMotores | null>(null);

  async function carregar() {
    try {
      setErro(null);
      const [p, m] = await Promise.all([
        api.get<{ perfis: Perfil[] }>("/perfis-processamento"),
        api.get<{ motores: MotorManifesto[] }>("/motores"),
      ]);
      setPerfis(p.perfis);
      setMotores(m.motores);
      if (!motor && m.motores.length > 0) setMotor(m.motores[0]!.versao);
    } catch (e) {
      setErro(e);
      notificarErroApi(e, "Falha ao carregar");
    }
  }

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function compDeTexto(txt: string): number | null {
    const m = /^(\d{2})\/?(\d{4})$/.exec(txt.trim());
    if (!m) return null;
    return Number(m[2]) * 100 + Number(m[1]);
  }

  async function criar() {
    const inicio = compDeTexto(vigInicio);
    if (!inicio) {
      notificarErroApi(new Error("Vigência inicial inválida — use MM/AAAA (ex.: 07/2026)"));
      return;
    }
    const fim = vigFim.trim() ? compDeTexto(vigFim) : null;
    if (vigFim.trim() && !fim) {
      notificarErroApi(new Error("Vigência final inválida — use MM/AAAA ou deixe em branco (vigência aberta)"));
      return;
    }
    try {
      setErro(null);
      await api.post("/perfis-processamento", {
        nome,
        descricao: descricao || undefined,
        motorVersion: motor,
        vigenciaInicio: inicio,
        vigenciaFim: fim,
        motivo: motivo || undefined,
      });
      notificarOk("Perfil criado.");
      setNome("");
      setVigInicio("");
      setVigFim("");
      setMotivo("");
      setDescricao("");
      await carregar();
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  async function alternarAtivo(p: Perfil) {
    try {
      setErro(null);
      await api.patch(`/perfis-processamento/${p.id}`, { ativo: !p.ativo });
      notificarOk(p.ativo ? `Perfil "${p.nome}" desativado.` : `Perfil "${p.nome}" ativado.`);
      await carregar();
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  async function comparar() {
    if (!diffDe || !diffPara) return;
    try {
      setErro(null);
      setDiff(await api.get<DiffMotores>(`/motores/diff?de=${encodeURIComponent(diffDe)}&para=${encodeURIComponent(diffPara)}`));
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  const opcoesMotores = motores.map((m) => ({ value: m.versao, label: m.versao }));

  return (
    <>
      <Title order={2} mb="xs">
        Perfis de processamento
      </Title>
      <Text size="sm" c="dimmed" mb="md">
        O perfil escolhe o motor, ancora a vigência (a partir de qual mês vale) e registra o motivo. A abertura da
        competência escolhe o perfil — o motor fica como fallback quando nenhum perfil cobre o mês. Motor e vigência
        inicial são imutáveis: mudou, cria-se um perfil novo.
      </Text>
      <ErroApiAlert erro={erro} />

      {!perfis ? (
        <Carregando linhas={3} />
      ) : perfis.length === 0 ? (
        <Vazio texto="Nenhum perfil cadastrado — crie o primeiro abaixo." />
      ) : (
        perfis.map((p) => (
          <Card withBorder shadow="xs" mb="sm" key={p.id}>
            <Group justify="space-between" wrap="wrap">
              <div>
                <Group gap="sm">
                  <Text fw={700}>{p.nome}</Text>
                  <Badge color={p.ativo ? "sillis" : "gray"} variant="light">
                    {p.ativo ? "ativo" : "inativo"}
                  </Badge>
                  <Badge variant="outline" color="gray">
                    {p.motorVersion}
                  </Badge>
                  <Badge variant="outline" color="gray">
                    {rotuloVigencia(p)}
                  </Badge>
                  {(p._count?.competencias ?? 0) > 0 && (
                    <Badge variant="outline" color="blue">
                      {p._count!.competencias} competência(s) pinada(s)
                    </Badge>
                  )}
                </Group>
                {p.descricao && (
                  <Text size="sm" c="dimmed" mt={4}>
                    {p.descricao}
                  </Text>
                )}
                {p.motivo && (
                  <Text size="sm" mt={2}>
                    <strong>Motivo:</strong> {p.motivo}
                  </Text>
                )}
              </div>
              <Switch
                checked={p.ativo}
                onChange={() => void alternarAtivo(p)}
                label={p.ativo ? "Ativo (elegível na abertura)" : "Inativo (registro histórico)"}
              />
            </Group>
          </Card>
        ))
      )}

      <Card withBorder shadow="xs" mt="lg" mb="lg">
        <Title order={4} mb="sm">
          Novo perfil
        </Title>
        <Group align="flex-end" wrap="wrap" gap="sm">
          <TextInput label="Nome" value={nome} onChange={(e) => setNome(e.currentTarget.value)} style={{ minWidth: 260 }} />
          <Select label="Motor" data={opcoesMotores} value={motor} onChange={(v) => setMotor(v ?? "")} />
          <TextInput
            label="Vigência inicial (MM/AAAA)"
            placeholder="07/2026"
            value={vigInicio}
            onChange={(e) => setVigInicio(e.currentTarget.value)}
          />
          <TextInput
            label="Vigência final (opcional)"
            placeholder="em aberto"
            value={vigFim}
            onChange={(e) => setVigFim(e.currentTarget.value)}
          />
        </Group>
        <Textarea
          label="Motivo (por que este perfil existe)"
          mt="sm"
          value={motivo}
          onChange={(e) => setMotivo(e.currentTarget.value)}
          placeholder="Ex.: nova tabela de INSS 2027 — motor@2027.1 passa a valer em 01/2027"
          autosize
          minRows={2}
        />
        <Textarea
          label="Descrição (opcional)"
          mt="sm"
          value={descricao}
          onChange={(e) => setDescricao(e.currentTarget.value)}
          autosize
          minRows={1}
        />
        {motor && (
          <Alert color="gray" variant="light" mt="sm">
            {motores.find((m) => m.versao === motor)?.descricao}
          </Alert>
        )}
        <Button mt="sm" onClick={() => void criar()} disabled={!nome.trim() || !motor || !vigInicio.trim()}>
          Criar perfil
        </Button>
      </Card>

      <Divider my="lg" />
      <Title order={3} mb="sm">
        Motores registrados — manifesto e diferenças
      </Title>
      <Text size="sm" c="dimmed" mb="sm">
        O que mudou em cada versão e por que a anterior ficou para trás — a diferença entre motores como dado, não
        só comentário de código.
      </Text>
      {motores.map((m) => (
        <Card withBorder shadow="xs" mb="sm" key={m.versao}>
          <Text fw={700}>{m.versao}</Text>
          <Text size="sm" c="dimmed">
            {m.descricao}
          </Text>
          {m.motivoNovaVersao && (
            <Text size="sm" mt={4}>
              <strong>Motivo da versão:</strong> {m.motivoNovaVersao}
            </Text>
          )}
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {m.mudancas.map((mud, i) => (
              <li key={i}>
                <Text size="sm">{mud}</Text>
              </li>
            ))}
          </ul>
        </Card>
      ))}

      <Card withBorder shadow="xs" mt="md">
        <Title order={4} mb="sm">
          Comparar duas versões
        </Title>
        <Group align="flex-end" gap="sm">
          <Select label="De" data={opcoesMotores} value={diffDe} onChange={(v) => setDiffDe(v ?? "")} />
          <Select label="Para" data={opcoesMotores} value={diffPara} onChange={(v) => setDiffPara(v ?? "")} />
          <Button variant="light" onClick={() => void comparar()} disabled={!diffDe || !diffPara}>
            Comparar
          </Button>
        </Group>
        {diff && (
          <div style={{ marginTop: 12 }}>
            {diff.motivoNovaVersao && (
              <Text size="sm" mb="xs">
                <strong>Motivo registrado de {diff.para}:</strong> {diff.motivoNovaVersao}
              </Text>
            )}
            <Text size="sm" fw={600} mt="sm">
              Fórmulas que mudam de versão
            </Text>
            {diff.formulasAlteradas.length === 0 ? (
              <Text size="sm" c="dimmed">
                Nenhuma — as duas executam as mesmas versões de fórmula.
              </Text>
            ) : (
              <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
                {diff.formulasAlteradas.map((f) => (
                  <li key={f.chave}>
                    <Text size="sm">
                      <strong>{f.chave}</strong>: {f.de} → {f.para}
                    </Text>
                  </li>
                ))}
              </ul>
            )}
            {(diff.parametrosExigidosAdicionados.length > 0 || diff.parametrosExigidosRemovidos.length > 0) && (
              <>
                <Text size="sm" fw={600} mt="sm">
                  Parâmetros exigidos (folha mensal)
                </Text>
                <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
                  {diff.parametrosExigidosAdicionados.map((p) => (
                    <li key={`+${p}`}>
                      <Text size="sm">+ {p}</Text>
                    </li>
                  ))}
                  {diff.parametrosExigidosRemovidos.map((p) => (
                    <li key={`-${p}`}>
                      <Text size="sm">− {p}</Text>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {diff.suportadasAlteradas.length > 0 && (
              <>
                <Text size="sm" fw={600} mt="sm">
                  Versões de fórmula suportadas
                </Text>
                <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
                  {diff.suportadasAlteradas.map((s) => (
                    <li key={s.chave}>
                      <Text size="sm">
                        <strong>{s.chave}</strong>
                        {s.adicionadas.length > 0 ? ` · passa a suportar: ${s.adicionadas.join(", ")}` : ""}
                        {s.removidas.length > 0 ? ` · deixa de suportar: ${s.removidas.join(", ")}` : ""}
                      </Text>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
